import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  aiRevenueForecasting,
  aiExpenseOptimization,
  aiCashFlowPrediction,
  aiScenarioModeling,
  aiFinancialHealthScore,
  aiEnhancedNPV
} from '@/lib/ai-financial-calculations';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Target,
  DollarSign,
  BarChart3,
  PieChart as PieChartIcon,
  Zap
} from 'lucide-react';

export function AIFinancialDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [assumptions, setAssumptions] = useState<any>(null);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Load financial assumptions with explicit typing
      const { data: assumptionsData, error: assumptionsError } = await supabase
        .from('financial_assumptions')
        .select('assumptions_data')
        .eq('user_id', user.id)
        .maybeSingle() as { data: { assumptions_data: any } | null, error: any };

      // Load balance sheet data
      const { data: balanceSheetData } = await supabase
        .from('balance_sheet_entries')
        .select('*')
        .eq('user_id', user.id)
        .order('period_end_date', { ascending: false });

      if (!assumptionsError && assumptionsData?.assumptions_data) {
        setAssumptions(assumptionsData.assumptions_data);
        generateAIAnalysis(assumptionsData.assumptions_data, balanceSheetData || []);
      } else {
        console.log('No financial assumptions found or error:', assumptionsError);
      }
    } catch (error: any) {
      console.log('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load financial data.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateAIAnalysis = async (assumptions: any, balanceSheetData: any[]) => {
    // Simulate historical data for demo purposes
    const historicalRevenue = Array.from({ length: 12 }, (_, i) => 
      100000 + (i * 5000) + (Math.random() - 0.5) * 10000
    );
    
    const historicalExpenses = Array.from({ length: 12 }, (_, i) => ({
      category: ['operating', 'administrative', 'marketing'][i % 3],
      amount: 30000 + (i * 2000) + (Math.random() - 0.5) * 5000
    }));

    // Run AI analysis
    const revenueForecasting = aiRevenueForecasting(historicalRevenue, assumptions, 12);
    const expenseOptimization = aiExpenseOptimization(historicalExpenses, assumptions);
    const cashFlowPrediction = aiCashFlowPrediction(
      historicalRevenue.map((revenue, i) => ({ 
        cashFlow: revenue - historicalExpenses[i]?.amount || 0 
      })), 
      assumptions, 
      12
    );
    const scenarioModeling = aiScenarioModeling(assumptions, 100);
    const healthScore = aiFinancialHealthScore(balanceSheetData, assumptions);
    const enhancedNPV = aiEnhancedNPV(assumptions);

    setAnalysisData({
      revenueForecasting,
      expenseOptimization,
      cashFlowPrediction,
      scenarioModeling,
      healthScore,
      enhancedNPV,
      historicalRevenue,
      historicalExpenses
    });
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Brain className="h-12 w-12 animate-pulse mx-auto mb-4 text-blue-600" />
          <p>AI is analyzing your financial data...</p>
        </div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Brain className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">No financial assumptions found. Please set up your financial assumptions first.</p>
          <Button className="mt-4" onClick={loadData}>
            Load Data
          </Button>
        </CardContent>
      </Card>
    );
  }

  const chartData = analysisData.revenueForecasting.forecast.map((value: number, index: number) => ({
    month: `Month ${index + 1}`,
    forecast: value,
    actual: index < 6 ? analysisData.historicalRevenue[analysisData.historicalRevenue.length - 6 + index] : null,
    cashFlow: analysisData.cashFlowPrediction.cashFlowForecast[index]
  }));

  return (
    <div className="space-y-6">
      {/* AI Health Score Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-blue-600" />
            AI Financial Health Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {analysisData.healthScore.score}
              </div>
              <div className="text-sm text-gray-600">Health Score</div>
              <Progress value={analysisData.healthScore.score} className="mt-2" />
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-2">
                {(analysisData.revenueForecasting.confidence * 100).toFixed(0)}%
              </div>
              <div className="text-sm text-gray-600">Forecast Confidence</div>
              <Badge variant={analysisData.revenueForecasting.confidence > 0.7 ? "default" : "secondary"}>
                {analysisData.revenueForecasting.trend}
              </Badge>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 mb-2">
                ${(analysisData.enhancedNPV.baseNPV / 1000).toFixed(0)}K
              </div>
              <div className="text-sm text-gray-600">Projected NPV</div>
              <Badge variant={analysisData.enhancedNPV.baseNPV > 0 ? "default" : "destructive"}>
                {analysisData.enhancedNPV.baseNPV > 0 ? "Positive" : "Negative"}
              </Badge>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 mb-2">
                {analysisData.cashFlowPrediction.riskAssessment.level.toUpperCase()}
              </div>
              <div className="text-sm text-gray-600">Risk Level</div>
              <Badge variant={
                analysisData.cashFlowPrediction.riskAssessment.level === 'low' ? 'default' :
                analysisData.cashFlowPrediction.riskAssessment.level === 'medium' ? 'secondary' : 'destructive'
              }>
                {(analysisData.cashFlowPrediction.riskAssessment.probability * 100).toFixed(0)}% Risk
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="forecasting" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="forecasting">Revenue Forecasting</TabsTrigger>
          <TabsTrigger value="optimization">Expense Optimization</TabsTrigger>
          <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
          <TabsTrigger value="scenarios">Scenarios</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="forecasting">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                AI Revenue Forecasting
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${Number(value).toLocaleString()}`, '']} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="actual" 
                      stroke="#8884d8" 
                      name="Historical Revenue"
                      connectNulls={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="forecast" 
                      stroke="#82ca9d" 
                      name="AI Forecast"
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">
                      ${(analysisData.revenueForecasting.forecast.reduce((a: number, b: number) => a + b, 0) / 1000).toFixed(0)}K
                    </div>
                    <p className="text-sm text-gray-600">Total Forecast (12 months)</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">
                      {analysisData.revenueForecasting.trend === 'growing' ? '+' : ''}
                      {((analysisData.revenueForecasting.forecast[11] / analysisData.revenueForecasting.forecast[0] - 1) * 100).toFixed(1)}%
                    </div>
                    <p className="text-sm text-gray-600">Annual Growth Rate</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">
                      {(analysisData.revenueForecasting.confidence * 100).toFixed(0)}%
                    </div>
                    <p className="text-sm text-gray-600">Confidence Level</p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="optimization">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                AI Expense Optimization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Optimization Opportunities</h3>
                  <div className="space-y-3">
                    {analysisData.expenseOptimization.savingOpportunities.map((opportunity: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium capitalize">{opportunity.category}</div>
                          <div className="text-sm text-gray-600">
                            Confidence: {(opportunity.confidence * 100).toFixed(0)}%
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600">
                            ${opportunity.potential.toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-600">Potential Saving</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4">Optimized Expense Projection</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analysisData.expenseOptimization.optimizedExpenses.map((expense: number, index: number) => ({
                        month: `M${index + 1}`,
                        expense
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Optimized Expense']} />
                        <Area type="monotone" dataKey="expense" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">AI Recommendations</h3>
                <div className="space-y-2">
                  {analysisData.expenseOptimization.recommendations.map((rec: string, index: number) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                      <span className="text-sm">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cashflow">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                AI Cash Flow Prediction
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${Number(value).toLocaleString()}`, '']} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="cashFlow" 
                      stroke="#82ca9d" 
                      name="Predicted Cash Flow"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`h-5 w-5 ${
                        analysisData.cashFlowPrediction.riskAssessment.level === 'low' ? 'text-green-600' :
                        analysisData.cashFlowPrediction.riskAssessment.level === 'medium' ? 'text-yellow-600' : 'text-red-600'
                      }`} />
                      <div className="text-lg font-bold capitalize">
                        {analysisData.cashFlowPrediction.riskAssessment.level} Risk
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Risk Assessment</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">
                      {(analysisData.cashFlowPrediction.riskAssessment.probability * 100).toFixed(0)}%
                    </div>
                    <p className="text-sm text-gray-600">Risk Probability</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">
                      {analysisData.cashFlowPrediction.riskAssessment.factors.length}
                    </div>
                    <p className="text-sm text-gray-600">Risk Factors</p>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">Risk Factors</h3>
                <div className="space-y-2">
                  {analysisData.cashFlowPrediction.riskAssessment.factors.map((factor: string, index: number) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-red-50 rounded">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="text-sm">{factor}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scenarios">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                AI Scenario Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {Object.entries(analysisData.scenarioModeling.scenarios).map(([scenario, data]: [string, any]) => (
                  <Card key={scenario}>
                    <CardContent className="pt-6">
                      <h3 className="text-lg font-semibold capitalize mb-3">{scenario} Scenario</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm">NPV:</span>
                          <span className="font-medium">${(data.npv / 1000).toFixed(0)}K</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm">IRR:</span>
                          <span className="font-medium">{(data.irr * 100).toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm">Payback:</span>
                          <span className="font-medium">{data.payback.toFixed(1)} years</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Probability Distribution</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analysisData.scenarioModeling.probabilityDistribution}
                          dataKey="probability"
                          nameKey="outcome"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          label={({ name, value }) => `${name}: ${(value * 100).toFixed(0)}%`}
                        >
                          {analysisData.scenarioModeling.probabilityDistribution.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4">Expected Value</h3>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-blue-600 mb-2">
                      ${(analysisData.scenarioModeling.expectedValue / 1000).toFixed(0)}K
                    </div>
                    <p className="text-gray-600">Expected NPV</p>
                    <Badge variant={analysisData.scenarioModeling.expectedValue > 0 ? "default" : "destructive"} className="mt-2">
                      {analysisData.scenarioModeling.expectedValue > 0 ? "Positive Expected Return" : "Negative Expected Return"}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                AI Financial Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Health Score Breakdown</h3>
                  <div className="space-y-3">
                    {analysisData.healthScore.components.map((component: any, index: number) => (
                      <div key={index} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">{component.metric}</span>
                          <span className="text-sm font-bold">{component.score.toFixed(0)}/100</span>
                        </div>
                        <Progress value={component.score} className="h-2" />
                        <div className="text-xs text-gray-600">
                          Weight: {(component.weight * 100).toFixed(0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4">AI Recommendations</h3>
                  <div className="space-y-3">
                    {[
                      ...analysisData.healthScore.insights,
                      ...analysisData.enhancedNPV.recommendations,
                      ...analysisData.cashFlowPrediction.recommendedActions
                    ].slice(0, 6).map((insight: string, index: number) => (
                      <div key={index} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                        <Brain className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{insight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">Sensitivity Analysis</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Variable</th>
                        <th className="text-left p-2">Impact on NPV</th>
                        <th className="text-left p-2">Sensitivity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisData.enhancedNPV.sensitivityAnalysis.map((item: any, index: number) => (
                        <tr key={index} className="border-b">
                          <td className="p-2 capitalize">{item.variable.replace(/([A-Z])/g, ' $1').trim()}</td>
                          <td className="p-2">${(item.impact / 1000).toFixed(0)}K</td>
                          <td className="p-2">
                            <Badge variant={item.impact > 50000 ? "destructive" : item.impact > 20000 ? "secondary" : "default"}>
                              {item.impact > 50000 ? "High" : item.impact > 20000 ? "Medium" : "Low"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
